import type { ReactNode } from "react";
import { AlertTriangle, Bot, Check, ShieldCheck } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatDate } from "../../lib/format-date";
import {
  CURRENT_LICENSE_STATUS,
  licenseIssues,
  stringList,
  UNCONFIRMED_LICENSE_STATUSES,
  type LicenseIssue,
} from "./license-issues";
import { getLicenseLimitations } from "./result-issues";
import type {
  VerificationAssessmentItem,
  VerificationCheck,
  VerificationResult,
} from "./types";

type Attachment = VerificationCheck["attachments"][number];
type Tone = "clear" | "attention" | "neutral";

const ATTACHMENT_FINDING_LABELS: Record<Attachment["result"], string> = {
  clear: "Verification clear",
  concern: "Review required",
  "missing-info": "Missing information — review required",
  error: "Verification error — review required",
};
const DISCIPLINARY_SCREENING_LABELS = {
  "not-checked": "Not checked",
  "no-findings": "No findings",
  findings: "Findings reported",
  incomplete: "Incomplete",
} as const;
type DisciplinaryScreeningStatus = keyof typeof DISCIPLINARY_SCREENING_LABELS;
const IDENTITY_MATCHED_LABEL = "Identity matched";
const TONE_STYLES: Record<Tone, string> = {
  clear: "border-green-200 bg-green-50/60 text-green-900",
  attention: "border-amber-200 bg-amber-50 text-amber-900",
  neutral: "border-slate-200 bg-slate-50 text-slate-800",
};

/**
 * One board result: the issues to resolve first, as the only warning on the
 * page, then the board's record beside the screening and attachment outcomes
 * it was derived from. Those supporting cards stay neutral so they never
 * compete with the issues.
 */
export function LicenseResult({
  result,
  assessment,
  attachments,
  evidenceActions,
  informational = false,
  showIssues = true,
}: {
  result: VerificationResult;
  assessment?: VerificationAssessmentItem;
  attachments: Attachment[];
  evidenceActions: ReactNode;
  /** A source that does not decide the license shows its record as neutral
   * information: its issues are not raised and its status is not flagged. */
  informational?: boolean;
  /** False when the page states this result's issues elsewhere. */
  showIssues?: boolean;
}) {
  const facts = result.facts;
  const disciplinaryLimitations = stringList(facts.disciplinaryLimitations);
  // Boards with a structured disciplinary view (NY OPMC) render it in full
  // below this result instead of as a summary card.
  const screening = assessment?.disciplinary
    ? null
    : {
        status: screeningStatus(facts.disciplinaryScreening),
        limitations: disciplinaryLimitations,
      };
  const showScreening = Boolean(
    screening && (screening.status || screening.limitations.length > 0),
  );
  const hasSideCards = showScreening || attachments.length > 0;
  const issues =
    informational || !showIssues
      ? []
      : licenseIssues({
          result,
          assessment,
          attachments,
          licenseLimitations: getLicenseLimitations({ result, assessment }),
        });
  return (
    <div className="space-y-4">
      <IssuesToReview issues={issues} />
      <div className={cn("grid gap-4", hasSideCards && "md:grid-cols-2")}>
        <BoardRecord
          result={result}
          evidenceActions={evidenceActions}
          informational={informational}
        />
        {hasSideCards && (
          <div className="space-y-3">
            {screening && showScreening && (
              <DisciplinaryScreeningCard {...screening} />
            )}
            <AutomatedVerificationCard attachments={attachments} />
          </div>
        )}
      </div>
    </div>
  );
}

function IssuesToReview({ issues }: { issues: LicenseIssue[] }) {
  if (issues.length === 0) {
    return null;
  }
  const [onlyIssue] = issues;
  return (
    <section
      aria-label="Issues to review"
      className={cn("space-y-3 rounded-lg border p-4", TONE_STYLES.attention)}
    >
      <h5 className="flex items-center gap-2 text-sm font-semibold">
        <AlertTriangle className="size-4 shrink-0" aria-hidden />
        {issues.length === 1
          ? onlyIssue.title
          : `${issues.length} issues to review`}
      </h5>
      {issues.length === 1 ? (
        <IssueDetails details={onlyIssue.details} className="pl-6" />
      ) : (
        <ul className="space-y-2.5 pl-6">
          {issues.map((issue, index) => (
            <li key={`${issue.title}-${index}`} className="space-y-0.5">
              <p className="text-sm font-medium">{issue.title}</p>
              <IssueDetails details={issue.details} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** Board actions can run to a paragraph; the full text is in the record
 * below, so each detail stays within two lines here. */
export function IssueDetails({
  details,
  className,
}: {
  details: string[];
  className?: string;
}) {
  return (
    <div className={cn("space-y-0.5", className)}>
      {details.map((detail) => (
        <p
          key={detail}
          className="line-clamp-2 text-xs leading-5"
          title={detail}
        >
          {detail}
        </p>
      ))}
    </div>
  );
}

function BoardRecord({
  result,
  evidenceActions,
  informational,
}: {
  result: VerificationResult;
  evidenceActions: ReactNode;
  informational: boolean;
}) {
  const facts = result.facts;
  const text = (value: unknown) =>
    typeof value === "string" && value ? value : null;
  const issued = text(facts.issuedAt);
  const expires = text(facts.expiresAt);
  const registeredThrough = text(facts.registrationExpiresAt);
  const status = text(facts.status);
  const secondaryStatus = text(facts.secondaryStatus);
  // A current license with an additional board status (such as a practice
  // limitation) needs review, so it must not read as plainly valid.
  let statusTone: Tone = "neutral";
  if (status === CURRENT_LICENSE_STATUS && !secondaryStatus) {
    statusTone = "clear";
  } else if (
    !informational &&
    status &&
    !UNCONFIRMED_LICENSE_STATUSES.has(status)
  ) {
    statusTone = "attention";
  }
  let dates: { label: string; value: string } | null = null;
  if (issued && expires) {
    dates = {
      label: "Issued / Expires",
      value: `${formatDate(issued)} — ${formatDate(expires)}`,
    };
  } else if (issued) {
    dates = { label: "Issued", value: formatDate(issued) };
  } else if (expires) {
    dates = { label: "Expires", value: formatDate(expires) };
  }
  const boardStatus = text(facts.statusRaw);
  return (
    <section aria-label="Board record" className="min-w-0 space-y-3">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 border-b pb-2 text-xs">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <h5 className="font-semibold uppercase tracking-wider text-muted-foreground">
            Board record
          </h5>
          {result.identityMatch === "matched" && (
            <span className="flex items-center gap-1 font-medium text-green-800">
              <Check className="size-3.5" aria-hidden />
              {IDENTITY_MATCHED_LABEL}
            </span>
          )}
        </div>
        <span className="text-muted-foreground">
          Checked {formatDate(result.observedAt)}
        </span>
      </div>
      <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-6 gap-y-2 text-sm">
        <RecordRow
          label="Licensing authority"
          value={text(facts.authorityName)}
        />
        <RecordRow label="Name on record" value={text(facts.licenseeName)} />
        <RecordRow
          label="License number"
          value={text(facts.licenseNumber)}
          className="font-mono"
        />
        <RecordRow label="License type" value={text(facts.licenseClass)} />
        {boardStatus && (
          <RecordRow
            label="Board status"
            value={
              <span className="inline-flex items-center gap-1.5">
                <span
                  className={cn(
                    "size-1.5 shrink-0 rounded-full",
                    statusTone === "clear" && "bg-green-600",
                    statusTone === "attention" && "bg-amber-500",
                    statusTone === "neutral" && "bg-slate-400",
                  )}
                  aria-hidden
                />
                <span
                  className={cn(
                    statusTone === "clear" && "text-green-800",
                    statusTone === "attention" && "text-amber-900",
                  )}
                >
                  {boardStatus}
                </span>
              </span>
            }
          />
        )}
        <RecordRow label="Additional status" value={secondaryStatus} />
        {dates && <RecordRow label={dates.label} value={dates.value} />}
        {registeredThrough && (
          <RecordRow
            label="Registered through"
            value={formatDate(registeredThrough)}
          />
        )}
      </dl>
      {evidenceActions}
    </section>
  );
}

function RecordRow({
  label,
  value,
  className,
}: {
  label: string;
  value: ReactNode;
  className?: string;
}) {
  if (!value) {
    return null;
  }
  return (
    <div className="contents">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className={cn("break-words font-medium", className)}>{value}</dd>
    </div>
  );
}

function DisciplinaryScreeningCard({
  status,
  limitations,
}: {
  status: DisciplinaryScreeningStatus | null;
  limitations: string[];
}) {
  // Findings are listed with the issues above; this card only records what
  // the screening covered and found.
  const tone: Tone = status === "no-findings" ? "clear" : "neutral";
  return (
    <section
      aria-label="Disciplinary screening"
      className={cn("space-y-2 rounded-lg border p-4", TONE_STYLES[tone])}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h5 className="flex items-center gap-2 text-sm font-semibold">
          <ShieldCheck className="size-4" aria-hidden />
          Disciplinary screening
        </h5>
        <span className="text-xs font-semibold uppercase tracking-wide">
          {status ? DISCIPLINARY_SCREENING_LABELS[status] : "Not specified"}
        </span>
      </div>
      <DisciplinaryScope limitations={limitations} />
    </section>
  );
}

/**
 * Most boards publish only part of their disciplinary history. That scope is
 * context for the result, not a problem, so it stays one click away; an
 * incomplete screening is listed with the issues instead.
 */
function DisciplinaryScope({ limitations }: { limitations: string[] }) {
  if (limitations.length === 0) {
    return null;
  }
  const list = (
    <ul className="list-disc space-y-1 pl-4 text-xs leading-5">
      {limitations.map((limitation) => (
        <li key={limitation}>{limitation}</li>
      ))}
    </ul>
  );
  return (
    <details className="space-y-1 text-xs">
      <summary className="cursor-pointer opacity-80">
        What the disciplinary check covers
      </summary>
      {list}
    </details>
  );
}

function AutomatedVerificationCard({
  attachments,
}: {
  attachments: Attachment[];
}) {
  if (attachments.length === 0) {
    return null;
  }
  const allClear = attachments.every(
    (attachment) => attachment.result === "clear",
  );
  return (
    <section
      aria-label="Automated verification"
      className={cn(
        "space-y-2 rounded-lg border p-4",
        allClear
          ? "border-primary/15 bg-primary/5 text-primary"
          : TONE_STYLES.neutral,
      )}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h5 className="flex items-center gap-2 text-sm font-semibold">
          <Bot className="size-4" aria-hidden />
          Automated verification
        </h5>
        <span className="text-xs font-semibold uppercase tracking-wide">
          {allClear ? "Attached" : "Needs review"}
        </span>
      </div>
      <div className="space-y-1 text-xs leading-5">
        {!allClear && (
          <p className="font-medium">Attached to the license record.</p>
        )}
        {attachments.map((attachment) => (
          <div key={attachment.verificationId} className="space-y-1">
            {attachment.result !== "clear" && (
              <p className="font-medium">
                {ATTACHMENT_FINDING_LABELS[attachment.result]}
              </p>
            )}
            {attachment.note && <p>{attachment.note}</p>}
          </div>
        ))}
      </div>
    </section>
  );
}

function screeningStatus(value: unknown): DisciplinaryScreeningStatus | null {
  if (typeof value === "string" && value in DISCIPLINARY_SCREENING_LABELS) {
    return value as DisciplinaryScreeningStatus;
  }
  return null;
}
